// The boot screen (public/index.html #boot-spinner): the wordmark, and on
// talent paths the slogan under it. It stays up until the app has something
// real to show — the sign-in check done — so a launch is ONE screen. Before
// 1.5.2 b47 it left after React's first paint and the in-app Loader (logo
// only) took over while sign-in was checked: the slogan flashed for half a
// second, then vanished (founder, TestFlight 46).

let hidden = false;

/**
 * Take the boot screen away after the next paint. Safe to call many times.
 * "app": the app's first real screen is up — on a cold open of the phone apps
 * the wordmark flies through to it (flyThrough below). "fade" (a crash, the
 * 15 s safety net) and everywhere else: the screen fades out.
 */
export function hideBootSpinner(exit: "app" | "fade" = "fade") {
  if (hidden) return;
  hidden = true;
  // Double rAF = after React's commit + a browser paint, so the app is on
  // screen before the boot screen leaves (no flash of nothing).
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const s = document.getElementById("boot-spinner");
      if (!s) return;
      if (exit === "app" && s.classList.contains("boot-cold-open") && flyThrough(s)) return;
      s.classList.add("boot-spinner--hide");
      setTimeout(() => s.remove(), 320);
    }),
  );
}

// The founder's pick for 1.5.3 ("B2", after Tribe's open): the wordmark,
// which has only breathed with light while sign-in was checked, flies toward
// you — scale 1→9 and out in 420 ms — while the background fades and the app
// shows through. It never gets smaller on the way: no dip before the zoom.
// Reduce Motion: no size change — it brightens to full, then dissolves.
// Returns false (the plain fade runs instead) where there is no Web Animations.
function flyThrough(s: HTMLElement): boolean {
  const stack = s.querySelector<HTMLElement>(".boot-stack");
  if (!stack || typeof stack.animate !== "function") return false;
  try {
    // Hold the breath where it is and leave from there: no jump in light or size.
    const now = getComputedStyle(stack);
    const from = now.opacity || "1";
    const size = !now.transform || now.transform === "none" ? "scale(1)" : now.transform;
    stack.style.animation = "none";
    stack.style.opacity = from;
    stack.style.transform = size;
    s.classList.add("boot-spinner--fly");
    let last: Animation;
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      stack.animate([{ opacity: from }, { opacity: 1 }], { duration: 160, easing: "ease-out", fill: "forwards" });
      last = s.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, delay: 160, easing: "ease", fill: "forwards" });
    } else {
      // The background as its own layer, faded by opacity: the same picture
      // as fading its colour, but it runs off the main thread like the
      // wordmark does, so it stays smooth while the app's first screen loads.
      const bg = document.createElement("div");
      bg.style.cssText = "position:absolute;top:0;right:0;bottom:0;left:0;background:#0E0F14";
      s.prepend(bg);
      s.style.background = "transparent";
      stack.style.position = "relative";
      s.querySelector<HTMLElement>(".boot-tagline")?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: "ease-out", fill: "forwards" });
      bg.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 380, easing: "ease-in", fill: "forwards" });
      last = stack.animate([{ transform: size, opacity: from }, { transform: "scale(9)", opacity: 0 }], { duration: 420, easing: "cubic-bezier(.55,0,.85,.35)", fill: "forwards" });
    }
    last.onfinish = last.oncancel = () => s.remove();
    return true;
  } catch {
    return false; // the plain fade takes it from here
  }
}

// Read by the boot screen's slogan script in public/index.html.
const TALENT_DEVICE_KEY = "rq_talent_device";

/** A talent signed in on this device (the boot screen's slogan may show at "/"). */
export function markTalentDevice() {
  try {
    localStorage.setItem(TALENT_DEVICE_KEY, "1");
  } catch {}
}
